import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Form, Button } from 'react-bootstrap';
import Select from 'react-select';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { ClientService } from '../../component/services/client.service';

interface FilterOption {
    label: string;
    value: string;
}

interface FiltersComponentProps {
    selectedShift: any;
    onFiltersChange: (filters: {
        states: FilterOption[];
        zones: FilterOption[];
        districts: FilterOption[];
        centers: FilterOption[];
        courses?: FilterOption[];
    }) => void;
    initialFilters?: {
        states?: FilterOption[];
        zones?: FilterOption[];
        districts?: FilterOption[];
        centers?: FilterOption[];
        courses?: FilterOption[];
    };
    showExamFilters?: boolean;
    showVerificationFilters?: boolean;
    showAttendanceFilters?: boolean;
    isVenueRecquired?: boolean;
}

export default function FiltersComponent({
    selectedShift,
    onFiltersChange,
    initialFilters = {},
    showExamFilters = false,
    showVerificationFilters = false,
    showAttendanceFilters = false,
    isVenueRecquired = false
}: FiltersComponentProps) {
    const [states, setStates] = useState<FilterOption[]>([]);
    const [zones, setZones] = useState<FilterOption[]>([]);
    const [districts, setDistricts] = useState<FilterOption[]>([]);
    const [centers, setCenters] = useState<FilterOption[]>([]);
    const [courses, setCourses] = useState<FilterOption[]>([]);

    const [selectedStates, setSelectedStates] = useState<FilterOption[]>(initialFilters.states || []);
    const [selectedZones, setSelectedZones] = useState<FilterOption[]>(initialFilters.zones || []);
    const [selectedDistricts, setSelectedDistricts] = useState<FilterOption[]>(initialFilters.districts || []);
    const [selectedCenters, setSelectedCenters] = useState<FilterOption[]>(initialFilters.centers || []);
    const [selectedCourses, setSelectedCourses] = useState<FilterOption[]>(initialFilters.courses || []);
    const [selectedVerification, setSelectedVerification] = useState<any>();
    const [verificationEnums, setVerificationEnums] = useState<any>();
    const [selectedAttendanceOption, setSelectedAttendanceOption] = useState<any>({ label: "All", value: "all" });
    const [isApplyingFilters, setIsApplyingFilters] = useState<boolean>(false);

    const attendanceOptions = [
        { label: "All", value: 'all' },
        { label: "Present", value: true },
        { label: "Absent", value: false },
    ];


    const getAllZones = async () => {
        if (!selectedShift || selectedStates.length === 0) return;

        try {
            const params = new URLSearchParams();
            selectedStates.forEach((state) => params.append("state[]", state.value));

            const res = await ClientService.getUniqueZone(selectedShift, "?" + params);
            if (res.status === 200) {
                setZones(res.data.map((zone: any) => ({
                    value: zone,
                    label: zone
                })));
            }
        } catch (err: any) {
            toast.error(err.response?.data || 'Failed to fetch zones');
        }
    };

    const getAllCourses = async () => {
        if (!selectedShift) return;

        try {
            const res = await ClientService.getAllCourses();
            if (res.status === 200) {
                setCourses(res.data?.data?.course.map((data: any) => ({
                    label: data,
                    value: data
                })));
            }
        } catch (err: any) {
            toast.error(err.response?.data || 'Failed to fetch courses');
        }
    };

    const getUniqueCenter = async () => {
        if (!selectedShift || selectedStates.length === 0 || selectedZones.length === 0 || selectedDistricts.length === 0) return;

        try {
            const params = new URLSearchParams();
            selectedZones.forEach((zone) => params.append("zone[]", zone.value));
            selectedStates.forEach((state) => params.append("state[]", state.value));
            selectedDistricts.forEach((district) => params.append("district[]", district.value));

            const res = await ClientService.getUniqueCenter2(selectedShift, "?" + params.toString());
            if (res.status === 200) {
                setCenters(
                    res.data?.centers.map((zone: any) => ({
                        value: zone?.centerPreference,
                        label: zone?.allocatedCenterId + " - " + zone?.centerPreference,
                    }))
                );
            }
        } catch (err: any) {
            toast.error(err.response?.data || 'Failed to fetch centers');
        }
    };

    const getUniqueState = async () => {
        if (!selectedShift) return;

        try {
            const params = new URLSearchParams();
            selectedZones.forEach((zone) => params.append("zone[]", zone.value));

            const res = await ClientService.getStates(selectedShift, "?" + params.toString());
            if (res.status === 200) {
                setStates(res.data.states?.map((zone: any) => ({
                    value: zone,
                    label: zone
                })));
            }
        } catch (err: any) {
            toast.error(err.response?.data || 'Failed to fetch states');
        }
    };

    const getUniqueCity = async () => {
        if (!selectedShift || selectedZones.length === 0 || selectedStates.length === 0) return;

        try {
            const params = new URLSearchParams();
            selectedZones.forEach((zone) => params.append("zone[]", zone.value));
            selectedStates.forEach((state) => params.append("state[]", state.value));

            const res = await ClientService.getCities(selectedShift, "?" + params.toString());
            if (res.status === 200) {
                setDistricts(res.data?.districts?.map((zone: any) => ({
                    value: zone,
                    label: zone
                })));
            }
        } catch (err: any) {
            toast.error(err.response?.data || 'Failed to fetch cities');
        }
    };

    const getVerificationEnums = async () => {
        await ClientService.getVerificationEnums().then(res => {
            if (res.status === 200) {
                // console.log(res.data, "enums")
                setVerificationEnums(Object.values(res.data.enums).filter((data: any) => data !== "gps").map((data: any) => {
                    return {
                        label: data === 'fingerprint' ? "Right Fingerprint" : data === "fingerprint2" ? "Left Fingerprint" : data,
                        value: data
                    }
                }))
            }
        }).catch(err => {
            toast.error(err.response.data);
        })
    }

    const isApplyButtonEnabled = selectedStates.length > 0 && selectedZones.length > 0 && selectedDistricts.length > 0 && (isVenueRecquired ? selectedCenters.length > 0 : true);

    const handleApplyFilters = async () => {
        if (!isApplyButtonEnabled) return;

        setIsApplyingFilters(true);

        try {
            if (selectedCenters.length === 0 && centers.length === 0) {
                await getUniqueCenter();
            }

            let payload: any = {
                states: selectedStates,
                zones: selectedZones,
                districts: selectedDistricts,
                centers: selectedCenters,
            };

            if (showExamFilters) {
                payload.courses = selectedCourses;
            }

            if (showVerificationFilters) {
                payload.verification = selectedVerification;
            }

            if( showAttendanceFilters) {
                payload.attendance = selectedAttendanceOption;
            }

            onFiltersChange(payload);
        } catch (error) {
            console.error('Error applying filters:', error);
        } finally {
            setIsApplyingFilters(false);
        }
    };

    const handleStateChange = (selectedOptions: any) => {
        let newSelectedStates: FilterOption[] = [];

        if (selectedOptions?.some((option: any) => option.value === "All")) {
            newSelectedStates = states.map((state) => ({
                label: state.label,
                value: state.value,
            }));
        } else {
            newSelectedStates = selectedOptions || [];
        }

        setSelectedStates(newSelectedStates);

        setSelectedZones([]);
        setSelectedDistricts([]);
        setSelectedCenters([]);
        setZones([]);
        setDistricts([]);
        setCenters([]);
    };

    const handleZoneChange = (selectedOptions: any) => {
        let newSelectedZones: FilterOption[] = [];

        if (selectedOptions?.some((option: any) => option.value === "All")) {
            newSelectedZones = zones.map((zone) => ({
                label: zone.label,
                value: zone.value,
            }));
        } else {
            newSelectedZones = selectedOptions || [];
        }

        setSelectedZones(newSelectedZones);

        setSelectedDistricts([]);
        setSelectedCenters([]);
        setDistricts([]);
        setCenters([]);
    };

    const handleDistrictChange = (selectedOptions: any) => {
        let newSelectedDistricts: FilterOption[] = [];

        if (selectedOptions?.some((option: any) => option.value === "All")) {
            newSelectedDistricts = districts.map((district) => ({
                label: district.label,
                value: district.value,
            }));
        } else {
            newSelectedDistricts = selectedOptions || [];
        }

        setSelectedDistricts(newSelectedDistricts);

        setSelectedCenters([]);
        setCenters([]);
    };

    const handleCenterChange = (selectedOptions: any) => {
        let newSelectedCenters: FilterOption[] = [];

        if (selectedOptions?.some((option: any) => option.value === "All")) {
            newSelectedCenters = centers.map((center) => ({
                label: center.label,
                value: center.value,
            }));
        } else {
            newSelectedCenters = selectedOptions || [];
        }

        setSelectedCenters(newSelectedCenters);
    };

    const handleCourseChange = (selectedOptions: any) => {
        let newSelectedCourses: FilterOption[] = [];

        if (selectedOptions?.some((option: any) => option.value === "All")) {
            newSelectedCourses = [...courses];
        } else {
            newSelectedCourses = selectedOptions || [];
        }

        setSelectedCourses(newSelectedCourses);
    };

    const handleVerificationChange = (selectedOptions: any) => {
        let newSelectedVerification: any = [];
        if (selectedOptions?.some((option: any) => option.value === "All")) {
            newSelectedVerification = verificationEnums;
        } else {
            newSelectedVerification = selectedOptions || [];
        }

        setSelectedVerification(newSelectedVerification);
    };

    useEffect(() => {
        if (selectedShift) {
            getUniqueState();
            if (showExamFilters) {
                getAllCourses();
            }
            if (showVerificationFilters) {
                getVerificationEnums();
            }
        }
    }, [selectedShift]);

    useEffect(() => {
        if (selectedStates.length > 0) {
            getAllZones();
        } else {
            setZones([]);
            setSelectedZones([]);
        }
    }, [selectedStates]);

    useEffect(() => {
        if (selectedZones.length > 0 && selectedStates.length > 0) {
            getUniqueCity();
        } else {
            setDistricts([]);
            setSelectedDistricts([]);
        }
    }, [selectedZones]);

    useEffect(() => {
        if (selectedStates.length > 0 && selectedZones.length > 0 && selectedDistricts.length > 0) {
            getUniqueCenter();
        } else {
            setCenters([]);
            setSelectedCenters([]);
        }
    }, [selectedDistricts]);

    return (
        <Card
            className="mt-3"
            style={{
                backgroundColor: "#f9f9f9",
                borderRadius: "12px",
                border: "none",
            }}
        >
            <Card.Body>
                <Row>
                    <Col md={3}>
                        <Form.Group>
                            <Form.Label className="text-muted fw-bold">
                                States <span className='text-danger'>*</span>
                            </Form.Label>
                            <Select
                                options={[
                                    { label: "All", value: "All" },
                                    ...(states || [])
                                ]}
                                onChange={handleStateChange}
                                value={selectedStates}
                                className="basic-multi-select"
                                classNamePrefix="select"
                                isClearable
                                isMulti
                                placeholder="Select States"
                            />
                        </Form.Group>
                    </Col>

                    <Col md={3}>
                        <Form.Group>
                            <Form.Label className="text-muted fw-bold">
                                Divisions <span className='text-danger'>*</span>
                            </Form.Label>
                            <Select
                                options={[
                                    { label: "All", value: "All" },
                                    ...(zones || [])
                                ]}
                                onChange={handleZoneChange}
                                value={selectedZones}
                                className="basic-multi-select"
                                classNamePrefix="select"
                                isClearable
                                isMulti
                                placeholder="Select Divisions"
                                isDisabled={selectedStates.length === 0}
                            />
                        </Form.Group>
                    </Col>

                    <Col md={3}>
                        <Form.Group>
                            <Form.Label className="text-muted fw-bold">
                                District <span className='text-danger'>*</span>
                            </Form.Label>
                            <Select
                                options={[
                                    { label: "All", value: "All" },
                                    ...(districts || [])
                                ]}
                                onChange={handleDistrictChange}
                                value={selectedDistricts}
                                className="basic-multi-select"
                                classNamePrefix="select"
                                isClearable
                                isMulti
                                placeholder="Select Districts"
                                isDisabled={selectedZones.length === 0}
                            />
                        </Form.Group>
                    </Col>

                    <Col md={3}>
                        <Form.Group>
                            <Form.Label className="text-muted fw-bold">
                                Venue {isVenueRecquired && <span className='text-danger'>*</span>}
                            </Form.Label>
                            <Select
                                options={[
                                    { label: "All", value: "All" },
                                    ...(centers || [])
                                ]}
                                onChange={handleCenterChange}
                                value={selectedCenters}
                                className="basic-multi-select"
                                classNamePrefix="select"
                                isClearable
                                isMulti
                                placeholder="Select Venues"
                                isDisabled={selectedDistricts.length === 0}
                            />
                        </Form.Group>
                    </Col>

                    {
                        showVerificationFilters &&
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label className="text-muted fw-bold">
                                    Verification Type
                                </Form.Label>
                                <Select
                                    options={verificationEnums}
                                    onChange={handleVerificationChange}
                                    value={selectedVerification}
                                    className="basic-multi-select"
                                    classNamePrefix="select"
                                    isMulti
                                />
                            </Form.Group>
                        </Col>
                    }

                    {
                        showExamFilters &&
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label className="text-muted fw-bold">
                                    Exam name
                                </Form.Label>
                                <Select
                                    options={[
                                        { label: "All", value: "All" },
                                        ...(courses || [])
                                    ]}
                                    onChange={handleCourseChange}
                                    value={selectedCourses}
                                    className="basic-multi-select"
                                    classNamePrefix="select"
                                    isClearable
                                    isMulti
                                    placeholder="Select Courses"
                                />
                            </Form.Group>
                        </Col>
                    }

                    {
                        showAttendanceFilters &&
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label className="text-muted fw-bold">
                                    Candidate Attendance
                                </Form.Label>
                                <Select
                                    options={attendanceOptions}
                                    onChange={(selectedOption: any) => {
                                        setSelectedAttendanceOption(selectedOption);
                                    }}
                                    value={selectedAttendanceOption}
                                    className="basic-multi-select"
                                    classNamePrefix="select"
                                    isClearable
                                />
                            </Form.Group>
                        </Col>
                    }
                </Row>

                <Row className="mt-3">
                    <Col md={8} className="d-flex align-items-center">
                        {!isApplyButtonEnabled && (
                            <small className="text-danger">
                                <FontAwesomeIcon icon={faFilter} className="me-2" />
                                Please select (States, Divisions, Districts {isVenueRecquired && " , Venue"} ) to apply filters
                            </small>
                        )}
                    </Col>
                    <Col md={4} className="d-flex justify-content-end">
                        <Button
                            variant="primary"
                            disabled={!isApplyButtonEnabled || isApplyingFilters}
                            onClick={handleApplyFilters}
                            style={{
                                borderRadius: "8px",
                                fontWeight: "600",
                                padding: "8px 24px"
                            }}
                        >
                            {isApplyingFilters ? (
                                <>
                                    <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                                    Applying Filters...
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faFilter} className="me-2" />
                                    Apply Filters
                                </>
                            )}
                        </Button>
                    </Col>
                </Row>
            </Card.Body>
        </Card>
    );
}

export type { FilterOption, FiltersComponentProps };
